import { useCallback } from 'react';
import { FEATURE_SECTIONS, SELECTED_LABEL_KEY } from '../../../common/constants/reportPlans';
import styles from './SelectReportTable.module.scss';
import { useTranslation } from 'react-i18next';

function CheckCircleFilled({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
    );
}

function CheckMark({ className }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

function Dash({ className }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
            aria-hidden="true"
        >
            <line x1="7" y1="12" x2="17" y2="12" />
        </svg>
    );
}

function SectionIcon({ className }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
        </svg>
    );
}

function DatabaseIcon({ className }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <ellipse cx="12" cy="5" rx="9" ry="3" />
            <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
            <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
        </svg>
    );
}

function CellValue({ value }) {
    if (value) {
        return <CheckCircleFilled className={styles.checkIcon} />;
    }

    if (!value) {
        return <Dash className={styles.dashIcon} />;
    }

    return <span className={styles.customText}>{value}</span>;
}

export default function SelectReportTable({ selectedPlan, onSelect, plans, vehicle }) {
    const { t } = useTranslation('reportPlans');

    const activePlans = plans;
    const isSingle = activePlans.length === 1;

    const handleSelect = useCallback(id => onSelect?.(id), [onSelect]);

    let rowIndex = 0;

    return (
        <div className={`${styles.card} ${isSingle ? styles.singlePlan : ''}`}>
            <div className={`${styles.planHeader} ${styles.animateHeader}`}>
                <div className={styles.planHeaderSpacer} />

                {activePlans.map(plan => {
                    const isSelected = selectedPlan === plan.id;
                    return (
                        <div
                            key={plan.id}
                            className={`${styles.planHeaderCell} ${isSelected ? styles.selected : ''}`}
                            onClick={() => handleSelect(plan.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleSelect(plan.id);
                                }
                            }}
                            aria-pressed={isSelected}
                        >
                            {plan.popular && <span className={styles.badge}>{t('plans.popular')}</span>}

                            <div className={styles.planTopZone}>
                                <span className={styles.planName}>{t(plan.nameKey)}</span>
                                <span className={styles.planSubName}>{t(plan.subtitleKey)}</span>
                                {plan.showsVehiclePhoto && vehicle?.photo && (
                                    <img className={styles.vehiclePhoto} src={vehicle.photo} alt={vehicle.name} />
                                )}
                                {plan.savings && <span className={styles.savingsBadge}>{t(plan.savings)}</span>}
                            </div>

                            <div className={styles.planBottomZone}>
                                <span className={styles.planPrice}>
                                    {plan.price} {t(plan.currencyKey)}
                                    <span className={styles.priceUnit}>
                                        {' / '}
                                        {t('perReport')}
                                    </span>
                                </span>
                                <button
                                    className={`${styles.selectBtn} ${isSelected ? styles.selectBtnSelected : ''}`}
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleSelect(plan.id);
                                    }}
                                >
                                    {isSelected && <CheckMark className={styles.btnCheckIcon} />}
                                    {isSelected ? t(SELECTED_LABEL_KEY) : t(plan.buttonLabelKey)}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {FEATURE_SECTIONS.map((section, sectionIdx) => (
                <div key={section.titleKey}>
                    <div className={styles.groupRow}>
                        <span className={styles.groupLabel}>
                            {sectionIdx === 0 ? (
                                <SectionIcon className={styles.sectionIcon} />
                            ) : (
                                <DatabaseIcon className={styles.sectionIcon} />
                            )}
                            {t(section.titleKey)}
                        </span>
                        {activePlans.map(plan => (
                            <div
                                key={plan.id}
                                className={`${styles.groupCellSpacer} ${
                                    selectedPlan === plan.id ? styles.selectedCol : ''
                                }`}
                            />
                        ))}
                    </div>

                    {section.features.map(feature => {
                        const delay = rowIndex * 0.03;
                        rowIndex++;

                        return (
                            <div
                                key={feature.labelKey}
                                className={`${styles.featureRow} ${styles.animateRow}`}
                                style={{ animationDelay: `${delay}s` }}
                            >
                                <div className={styles.featureLabel}>{t(feature.labelKey)}</div>

                                {activePlans.map(plan => (
                                    <div
                                        key={plan.id}
                                        className={`${styles.featureCell} ${
                                            selectedPlan === plan.id ? styles.cellHighlight : ''
                                        }`}
                                    >
                                        <CellValue value={feature[plan.id]} />
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}
